import { std } from '@funkey/shared';
import { useEffect, useMemo, useState } from 'react';
export declare namespace useHoverState {
  export type Options = {
    /** Only runs when the hover state changes.
     * Unlike `useEffect(() => {...}, [isHovering])`, which also runs onMount */
    onHoverChange?(isHovering: boolean): void;
  };
}
/** @example ```typescript
 * const elRef = useRef<HTMLDivElement>(null!);
 * const isHovering = useHoverState(elRef);
 *
 * return <div ref={elRef} />
 * ``` */
export function useHoverState(elementRef: React.RefObject<HTMLElement>, options: useHoverState.Options): boolean;
/** @example ```typescript
 * const [isHovering, props] = useHoverState();
 *
 * return <div {...props} />
 * ``` */
export function useHoverState(props?: useHoverState.Options): [isHovering: boolean, hoverProps: {
  onMouseOver(): void;
  onMouseLeave(): void;
}];
export function useHoverState(...args: unknown[]): unknown {
  const isRefMode = std.Object.isRecord(args[0]) && 'current' in args[0];
  const [ref, props] = isRefMode ? [args[0] as unknown as React.RefObject<HTMLElement>, args[1] as useHoverState.Options] : [null, args[0] as useHoverState.Options];
  const [isHovering, setIsHovering] = useState(false);
  const sdk = useMemo(() => {
    function syncHover(value: boolean) {
      setIsHovering(prev => {
        if (prev !== value) props?.onHoverChange?.(value);
        return value;
      });
    }
    return {
      onMouseOver() {
        syncHover(true);
      },
      onMouseLeave() {
        syncHover(false);
      }
    } satisfies Partial<React.DOMAttributes<HTMLElement>>;
  }, [setIsHovering]);
  useEffect(() => {
    if (!ref) return;
    const abort = new AbortController();
    ref.current.addEventListener('mouseover', sdk.onMouseOver, {
      signal: abort.signal
    });
    ref.current.addEventListener('mouseleave', sdk.onMouseLeave, {
      signal: abort.signal
    });
    return () => abort.abort();
  }, [ref]);
  return isRefMode ? isHovering : [isHovering, sdk];
}